/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useState } from 'react'

import { LocalizationProvider, DatePicker } from '@mui/lab'
import AdapterMoment from '@mui/lab/AdapterMoment'
import { Col, Span } from 'components'
import { ClearInputIcon } from 'components/Icons'
import moment, { Moment } from 'moment'
import 'moment/locale/pt-br'
import { Controller, Control } from 'react-hook-form'
import { DateTime } from 'services/database/reportsDatabase'
import { COLORS, SPACING } from 'utils'

import {
  InputLabelCustom,
  ITextField,
  TextFieldStyled,
  TextFieldContainer,
  CalendarButton,
  ClearDateButton
} from './DatePicker.styles'

moment.locale('pt-br')

export type Views = 'day' | 'month' | 'year'

type TDate = Date | string | null | Moment

export interface DatePickerProps extends ITextField {
  control?: Control<any, any>
  formValue?: string
  value?: Date | string | null | Moment
  onChange?: (_event: any) => void
  label?: string
  placeholder?: string
  defaultValue?: Date | string
  views?: Views[]
  inputFormat?: string
  mask?: any
  type?: 'day' | 'monthAndYear' | 'dayMonthYear' | 'year' | 'month' | 'default'
  disabled?: boolean
  minDate?: any
  maxDate?: any
  removeButton?: boolean
  errors?: any
  onBlur?: any
  id?: string
  customError?: any
  allowTyping?: boolean
  flex?: string
  onValid?: (_date: any, teste?: boolean) => void
  showClearButton?: boolean
}

export const Picker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  label = '',
  marginBottom,
  marginLeft,
  marginRight,
  marginTop,
  control,
  removeButton = false,
  formValue,
  placeholder = 'dd/mm/aaaa',
  width,
  defaultValue,
  views,
  inputFormat = 'DD/MM/YYYY',
  mask = '__/__/____',
  type = 'default',
  disabled,
  minDate,
  maxDate,
  errors,
  onBlur = () => null,
  customError,
  id,
  allowTyping = true,
  flex,
  onValid,
  showClearButton = true
}) => {
  const [inputValue, setInputValue] = useState(value)
  const [stateError, setStateError] = useState(null)

  let hasError: any

  if (onValid) {
    hasError = stateError
  } else {
    hasError = (errors && errors[formValue]) || customError
  }

  // disableMaskedInput=false tira a necessidade do maxLength forçado aqui,
  // além de manter a mascára quando usado a prop inputFormat MM/YYYY
  const TYPES: ITypesMap = {
    day: {
      inputFormat: 'D',
      mask: '_',
      maxLength: 2,
      placeholder: 'dd',
      views: ['day']
    },
    dayMonthYear: {
      inputFormat: 'DD/MM/YYYY',
      mask: '__/__/____',
      maxLength: null,
      placeholder: 'dd/mm/aaaa',
      views: ['day', 'year']
    },
    default: {
      inputFormat: 'DD/MM/YYYY',
      mask: '__/__/____',
      maxLength: null,
      placeholder: 'dd/mm/aaaa',
      views: null
    },
    month: {
      inputFormat: 'MM',
      mask: '__',
      maxLength: 2,
      placeholder: 'mm',
      views: ['month']
    },
    monthAndYear: {
      inputFormat: 'MM/YYYY',
      mask: '__/____',
      maxLength: 7,
      placeholder: 'mm/aaaa',
      views: ['month', 'year']
    },
    year: {
      inputFormat: 'YYYY',
      mask: '____',
      maxLength: 4,
      placeholder: 'aaaa',
      views: ['year']
    }
  }

  const validateRange = (date: Moment) => {
    const start = moment(maxDate).isValid()
      ? date?.isSameOrBefore(maxDate)
      : true
    const end = moment(minDate).isValid() ? date?.isSameOrAfter(minDate) : true
    const range = start && end

    return { end, range, start }
  }

  const handleError = (date: any, keyboard: string): IHandleError => {
    const ERROR = {
      date: 'Data inválida',
      day: 'Dia inválido',
      invalidEnd: 'Fim menor que inicio',
      invalidStart: 'Inicio maior que fim',
      month: 'Mês inválido',
      year: 'Ano inválido'
    }

    const mask = date?._f
    const fieldIsFull = mask?.length === keyboard?.length

    let error: IError = null

    if (fieldIsFull && !validateRange(date).start && maxDate) {
      error = { message: ERROR['invalidStart'] }
    }

    if (fieldIsFull && !validateRange(date).end && minDate) {
      error = { message: ERROR['invalidEnd'] }
    }

    const getValueByMask = (target: string) => {
      const arr = []

      for (const i in mask) {
        if (target[0] === mask[i]) {
          arr.push(keyboard?.[i] || null)
        }
      }

      return arr.join('')
    }

    const Day = getValueByMask('D')
    const Month = getValueByMask('M')
    const Year = getValueByMask('Y')

    if (Number(Day[0]) > 3 || Number(Day) > 31 || Day == '00') {
      error = { message: ERROR['day'] }
    }

    if (Number(Month[0]) > 1 || Month === '00') {
      error = { message: ERROR['month'] }
    }

    if (Number(Month[0]) !== 0 && Number(Month[1]) > 2) {
      error = { message: ERROR['month'] }
    }

    if (Number(Year[0]) === 0 || Number(Year[0]) > 2) {
      error = { message: ERROR['year'] }
    }

    const Y1 = Year[0] || ''
    const Y2 = Year[1] || ''
    const YY__ = Y1 + Y2

    if (YY__?.length >= 2 && Number(YY__) > 20) {
      error = { message: ERROR['year'] }
    }

    if (YY__?.length >= 2 && Number(YY__) < 17) {
      error = { message: ERROR['year'] }
    }

    if (fieldIsFull && !date?.isValid()) {
      error = { message: 'Data inválida' }
    }

    setStateError(error)

    return { error, fieldIsFull }
  }

  const handleOnBlur = (e: IFocusEvent) => {
    // Se não passar o onChange na prop onValid
    if (!onValid) {
      return onBlur(e)
    }

    // Default: DD/MM/YYY, precisa pegar da prop pro isValid() validar corretamente
    const format = TYPES[type]?.inputFormat || inputFormat
    const keyboard = e.target.value
    const date = moment(keyboard, format, true)
    const { error } = handleError(date, keyboard)

    // Se a data no campo não for válida ou tiver erro,
    // volta pra data (válida) anterior ou limpa
    if (!date.isValid() || error) {
      setInputValue(value || null)
      setStateError(null)

      return
    }

    onBlur(e)
  }

  const handleOnChange = (date: any, keyboard: string, onChange: any) => {
    // Comportamento padrão, onChange a cada tecla/data selecionada
    if (!onValid) {
      return onChange(date)
    }

    // Traz e já seta o erro no estado
    const { error, fieldIsFull } = handleError(date, keyboard)

    // Valor visual do input, guardado mesmo se inválido
    setInputValue(date)

    // Roda o onChange só se a data for válida e tiver sido digitada
    if ((fieldIsFull && !error && keyboard) || !date) {
      onValid(date)
      setStateError(null)
      return
    }
  }

  const handleClearButton = (onChange: any) => {
    if (!onValid) {
      return onChange(null)
    }

    onValid(null)
    setInputValue(null)
    setStateError(null)
  }

  const getDatePicker = (date: any, onChange: any) => {
    return (
      <DatePicker
        disableMaskedInput={false}
        views={TYPES[type]?.views || views}
        inputFormat={TYPES[type]?.inputFormat || inputFormat}
        mask={TYPES[type]?.mask || mask}
        value={date}
        disabled={disabled}
        minDate={minDate ? moment(minDate) : null}
        maxDate={maxDate ? moment(maxDate) : null}
        onChange={(date, keyboard) => handleOnChange(date, keyboard, onChange)}
        onAccept={onValid || onChange}
        allowSameDateSelection
        PopperProps={{ placement: 'auto-start' }}
        renderInput={({ inputProps, InputProps, ...rest }) => {
          return (
            <TextFieldContainer>
              {!allowTyping && (
                <CalendarButton>{InputProps.endAdornment}</CalendarButton>
              )}
              <TextFieldStyled
                allowTyping={allowTyping}
                hasError={hasError}
                id={id}
                onFocus={(e) => (!allowTyping ? e.target.blur() : null)}
                onBlur={handleOnBlur}
                inputProps={{
                  ...inputProps,
                  ...getPropIfExists(
                    'placeholder',
                    TYPES[type]?.placeholder || placeholder
                  ),
                  ...getPropIfExists(
                    'maxLength',
                    TYPES[type]?.maxLength || null
                  )
                }}
                variant="standard"
                width={width}
                InputProps={{
                  ...InputProps,
                  ...(!allowTyping && { endAdornment: null })
                }}
                {...rest}
              />
              {(showClearButton || !allowTyping) &&
                date &&
                !disabled &&
                !removeButton && (
                  <ClearDateButton onClick={() => handleClearButton(onChange)}>
                    <ClearInputIcon />
                  </ClearDateButton>
                )}
            </TextFieldContainer>
          )
        }}
      />
    )
  }

  const getError = () => {
    return (
      hasError && (
        <Span
          text={hasError?.message || customError || 'Erro.'}
          color={COLORS.error}
          marginTop={SPACING.spacing5}
        />
      )
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} locale="pt-br">
      <Col
        marginTop={marginTop}
        marginBottom={marginBottom}
        marginLeft={marginLeft}
        marginRight={marginRight}
        flex={flex}
      >
        <InputLabelCustom hasError={hasError}>{label}</InputLabelCustom>
        {control ? (
          <Controller
            control={control}
            name={formValue}
            defaultValue={defaultValue}
            render={({ field: { value, onChange } }) => (
              <>
                {getDatePicker(value, onChange)}
                {getError()}
              </>
            )}
          />
        ) : (
          <>
            {getDatePicker(onValid ? inputValue : value, onChange)}
            {getError()}
          </>
        )}
      </Col>
    </LocalizationProvider>
  )
}

const getPropIfExists = (name: string, value: any) =>
  value ? { [name]: value } : {}

interface IType {
  inputFormat: string
  mask: string
  maxLength: number
  placeholder: string
  views: Views[]
}

interface ITypesMap {
  [type: string]: IType
}

type IFocusEvent = React.FocusEvent<
  HTMLInputElement | HTMLTextAreaElement,
  Element
>
interface IErrors {
  [x: string]: any
}
interface IError {
  message: string
}
interface IHandleError {
  fieldIsFull: boolean
  error: IError
}
